'use client';

import { type MouseEventHandler, forwardRef, useCallback, useMemo, useState } from 'react';

import { css, cx } from '@/styled-system/css';

import { Slot } from '../slot';
import { UpsellRootContext } from './context/upsell-root.context';
import { upsellRootRecipe } from './styles/upsell-root.styles';
import type { UpsellRootPropsInterface } from './types/upsell-root.types';

/**
 * @since 10.0.0
 */
export const UpsellRoot = forwardRef<HTMLDivElement, UpsellRootPropsInterface>(
  (props: UpsellRootPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      className,
      css: cssProp,
      elevated = true,
      open: controlledOpen,
      onOpenChange: setControlledOpen,
      tone = 'neutral',
      ...rest
    } = props;

    const [uncontrolledOpen, setUncontrolledOpen] = useState<boolean | undefined>(
      typeof controlledOpen === 'boolean' ? undefined : true,
    );
    const open = typeof controlledOpen === 'boolean' ? controlledOpen : uncontrolledOpen;

    const onOpenChange = useCallback<MouseEventHandler<HTMLButtonElement>>(
      (event) => {
        if (typeof controlledOpen === 'boolean') {
          setControlledOpen?.(false, event);
        } else {
          setUncontrolledOpen(false);
          setControlledOpen?.(false, event);
        }
      },
      [controlledOpen, setControlledOpen],
    );

    const styles = css(upsellRootRecipe.raw({ elevated, tone }), cssProp);

    const Component = asChild ? Slot : 'div';

    const contextValue = useMemo(() => ({ open, onOpenChange, tone }), [open, onOpenChange, tone]);

    return (
      <UpsellRootContext.Provider value={contextValue}>
        {open ? (
          <Component
            ref={forwardedRef}
            data-c11n-component="Upsell.Root"
            data-c11n-version={__C11N_VERSION__}
            className={cx('c11n', styles, className)}
            {...rest}
          />
        ) : null}
      </UpsellRootContext.Provider>
    );
  },
);

UpsellRoot.displayName = 'Upsell.Root';
